import { Icon, ModalV2 } from 'components';
import { Api, capitalize, formatLocale, titleToLocaleKey } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { canUpgradeSubscription } from 'lib/subscriptionUpgrade';
import { featureLabel } from './activity';
import BillingTimeline from './BillingTimeline';
import CancelView from './CancelView';
import RefundView from './RefundView';
import {
  formatAmount,
  getFormattedAmountWithInterval,
  getPaymentMethodDisplay,
  getStatusStyle
} from './helpers';

const SubscriptionDetailModal = ({ visible, closeModal, sub, isMobile }) => {
  const [view, setView] = useState('details');
  const [cancelReason, setCancelReason] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [events, setEvents] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [refundRequest, setRefundRequest] = useState(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  async function fetchBillingHistory() {
    if (!sub?.teamId) {
      setEvents([]);
      setRefundRequest(null);
      return false;
    }

    const res = await Api.get(
      `/subscriptionPlans/billingHistory?teamId=${sub.teamId}&subscriptionId=${sub.subscriptionId}`
    );
    if (!res) return false;

    if (res?.data?.events) {
      setEvents(res.data.events);
    }
    if (res?.data?.refundRequest !== undefined) {
      setRefundRequest(res.data.refundRequest);
    }
    return true;
  }

  async function fetchTimeline() {
    if (!sub?.subscriptionId) {
      setTimeline(null);
      return;
    }
    const res = await Api.get(`/subscriptionPlans/timeline?subscriptionId=${sub.subscriptionId}`);
    setTimeline(res?.data || null);
  }

  useEffect(() => {
    fetchBillingHistory();
    fetchTimeline();
  }, [sub?.teamId, sub?.subscriptionId]);

  if (!sub) return null;
  const normalizedStatus = String(sub.status || '').toLowerCase();

  const style = getStatusStyle(normalizedStatus);

  function handleClose() {
    setView('details');
    setCancelReason('');
    setRefundReason('');
    setRefundRequest(null);
    closeModal();
  }

  async function handleCancel() {
    if (!sub?.provider || !sub?.teamId) return;

    const body = { teamId: sub.teamId };
    if (cancelReason) body.reason = cancelReason;
    const res = await Api.post(`/subscriptionPlans/${sub.provider}/unsubscribe`, body);
    if (!res) return;

    const successMessage =
      // (typeof res?.data?.message === 'string' && res.data.message) ||
      // (typeof res?.msg === 'string' && res.msg) ||
      t('billing.scheduledForCancellation');

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: successMessage }
    });
    handleClose();
    setTimeout(() => {
      window.location.href = '/billing?tab=1';
    }, 1500);
  }

  async function downloadInvoice(billingEventId) {
    if (!billingEventId) return;
    const blob = await Api.getBlob(`/billing/invoice?billingEventId=${billingEventId}`);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${billingEventId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleDownloadInvoice(e) {
    downloadInvoice(e.currentTarget.dataset.id);
  }

  async function handleRefund() {
    const body = { subscriptionId: sub.subscriptionId };
    if (refundReason) body.reason = refundReason;
    const res = await Api.post('/billing/requestRefund', body);
    if (!res) return;
    const refreshed = await fetchBillingHistory();
    if (!refreshed) {
      setRefundRequest({ status: 'pending', reason: refundReason });
    }
    setRefundReason('');
    window.location.href = '/billing?tab=1';
  }

  if (view === 'cancel') {
    return (
      <CancelView
        visible={visible}
        closeModal={handleClose}
        sub={sub}
        isMobile={isMobile}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        onCancel={handleCancel}
        onBack={() => setView('details')}
      />
    );
  }

  if (view === 'refund') {
    return (
      <RefundView
        visible={visible}
        closeModal={handleClose}
        sub={sub}
        isMobile={isMobile}
        refundReason={refundReason}
        setRefundReason={setRefundReason}
        onRefund={handleRefund}
        onBack={() => setView('details')}
      />
    );
  }

  const isOneTime = sub.billingCycle === 'yearly' || !sub.billingCycle;
  const isRefunded = normalizedStatus === 'refunded';
  const isCancelledOrOneTime = normalizedStatus === 'cancelled' || isOneTime;
  const isCancelDisabled = normalizedStatus === 'suspended' || normalizedStatus === 'failed' || isRefunded;
  const canUpgrade = canUpgradeSubscription(sub);
  const hasCollectedPayment = events.some(
    (e) =>
      ['subscribed', 'renewed', 'payment_succeeded'].includes(e.eventType) &&
      Number(e.amount) > 0
  );
  const isRefundEligibleStatus = ['failed', 'suspended'].includes(normalizedStatus);
  const canShowRefundSection = (isRefundEligibleStatus && hasCollectedPayment) || !!refundRequest;
  const hasRefundRequest = !!refundRequest;

  const details = [
    {
      label: t('billing.amount'),
      value: isOneTime ? formatAmount(sub.amount, sub.currency) : getFormattedAmountWithInterval(sub),
      bold: true
    },
    {
      label: t('billing.billingCycle'),
      value: isOneTime ? t('billing.oneTime', { defaultValue: 'One-time' }) : capitalize(sub.billingCycle)
    },
    { label: t('billing.paymentMethod'), value: getPaymentMethodDisplay(sub) },
    { label: t('billing.started'), value: formatLocale(sub.startedAt, 'MMM d, yyyy') },
    {
      label: t('billing.currentPeriod'),
      value: `${sub.billingCycle === 'monthly' ? formatLocale(sub.currentPeriodStart, 'MMM d') : formatLocale(sub.currentPeriodStart, 'MMM d, yyyy')} — ${formatLocale(sub.currentPeriodEnd, 'MMM d, yyyy')}`
    },
    ...(isOneTime
      ? [{ label: t('billing.expires', { defaultValue: 'Expires' }), value: formatLocale(sub.currentPeriodEnd, 'MMM d, yyyy') }]
      : normalizedStatus !== 'cancelled'
        ? [{ label: t('billing.nextPayment'), value: formatLocale(sub.nextRenewalAt, 'MMM d, yyyy') }]
        : []
    ),
    ...(!isOneTime
      ? [{ label: t('billing.renewals'), value: String(sub.renewalCount) }]
      : []
    ),
    { label: t('billing.provider'), value: capitalize(sub.provider) }
  ];

  return (
    <ModalV2
      visible={visible}
      closeModal={handleClose}
      width={isMobile ? 'calc(100% - 20px)' : '560px'}
    >
      <div className={`${isMobile ? 'p-0 py-4' : 'p-6'} space-y-5`}>
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 font-[CeraPro]">{sub.teamName}</h3>
            <p className="text-sm text-gray-500 font-[CeraPro] mt-0.5">
              {t(`subscription.plans.${titleToLocaleKey(sub.planTitle)}`, {
                defaultValue: sub.planTitle
              })}
            </p>
          </div>

          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${style.bg} ${style.text} ${style.ring}`}
          >
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`} />
            {t(`billing.status_${normalizedStatus}`, { defaultValue: capitalize(normalizedStatus) })}
          </span>
        </div>

        {/* Details grid */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm divide-y divide-gray-100 overflow-hidden">
          {details.map((row, i) => (
            <div
              key={i}
              className={`flex items-start justify-between px-4 py-3 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'} gap-3`}
            >
              <span className="text-[11px] text-gray-800 font-medium font-[CeraPro] shrink-0 whitespace-nowrap mt-0.5">
                {row.label}
              </span>
              <span
                className={`text-[13px] font-[CeraPro] text-gray-900 text-right leading-snug ${row.bold ? 'font-bold' : ''}`}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {/* Entitlements */}
        {sub.entitlements?.length > 0 && (
          <div>
            <p className="text-xs text-gray-800 font-medium font-[CeraPro] mb-2">
              {t('billing.includedFeatures')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {sub.entitlements.map((e) => (
                <span
                  key={e.key}
                  className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 font-[CeraPro]"
                >
                  {featureLabel(t, e)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Billing history: what was charged, and why */}
        {timeline && timeline.entries?.length > 0 && (
          <div>
            <p className="text-xs text-gray-800 font-medium font-[CeraPro] mb-2">
              {t('billing.recentActivity')}
            </p>
            <BillingTimeline timeline={timeline} audience="user" onDownloadInvoice={downloadInvoice} />
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-2.5 pt-1">
          <div className="flex gap-2.5">
            <button
              onClick={
                isCancelledOrOneTime
                  ? handleClose
                  : isCancelDisabled
                    ? undefined
                    : () => setView('cancel')
              }
              disabled={isCancelDisabled}
              className={`flex-1 h-11 rounded-full text-sm font-bold font-[CeraPro] transition-all ${
                isCancelledOrOneTime
                  ? 'bg-gray-900 text-white hover:opacity-80 active:scale-[0.98]'
                  : isCancelDisabled
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#FA3F58] text-white hover:opacity-80 active:scale-[0.98]'
              }`}
            >
              {isCancelledOrOneTime ? t('billing.goBack') : t('billing.cancelSubscription')}
            </button>
            <button
              onClick={() =>
                canUpgrade && navigate(`/subscription-plans?teamId=${sub.teamId}`)
              }
              disabled={!canUpgrade}
              className={`flex-1 h-11 rounded-full border-2 text-sm font-bold font-[CeraPro] transition-all ${
                canUpgrade
                  ? 'border-black bg-black text-white hover:opacity-80 active:scale-[0.98] cursor-pointer'
                  : 'border-gray-300 bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {t('billing.upgradeSubscription')}
            </button>
          </div>

          {/* Refund request button — only when payment was actually collected */}
          {canShowRefundSection && (
            <div>
              {!hasRefundRequest && (
                <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-2 space-y-2">
                  <div className="px-2">
                    <p className="text-xs font-semibold text-amber-900 font-[CeraPro]">
                      {t('billing.refundRequestPromptTitle', { defaultValue: 'Need a refund?' })}
                    </p>
                    <p className="text-[11px] text-amber-800 font-[CeraPro] mt-0.5">
                      {t('billing.refundRequestPromptDesc', {
                        defaultValue: 'If your payment was collected but subscription activation failed, you can request a refund for admin review.'
                      })}
                    </p>
                  </div>
                  <button
                    onClick={() => setView('refund')}
                    className="w-full h-11 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold font-[CeraPro] shadow-sm hover:shadow-md active:scale-[0.98] transition-all"
                  >
                    {t('billing.requestRefund', { defaultValue: 'Request Refund' })}
                  </button>
                </div>
              )}
              {refundRequest?.status === 'pending' && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-bold text-amber-900 font-[CeraPro]">
                    {t('billing.refundPending', { defaultValue: 'Refund Requested — Pending Approval' })}
                  </p>
                  <p className="text-xs text-amber-800 font-[CeraPro] mt-1">
                    {t('billing.refundPendingMessage', {
                      defaultValue: 'Your request is under review. We will update this status after admin action.'
                    })}
                  </p>
                  {refundRequest?.createdAt && (
                    <p className="text-[11px] text-amber-700 font-[CeraPro] mt-2">
                      {t('billing.requestedOn', { defaultValue: 'Requested on' })}: {formatLocale(refundRequest.createdAt, 'MMM d, yyyy')}
                    </p>
                  )}
                  {refundRequest?.reason && (
                    <p className="text-[11px] text-amber-700 font-[CeraPro] mt-1">
                      {t('billing.yourNote', { defaultValue: 'Your note' })}: {refundRequest.reason}
                    </p>
                  )}
                </div>
              )}
              {refundRequest?.status === 'rejected' && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                  <p className="text-sm font-bold text-red-700 font-[CeraPro]">
                    {t('billing.refundRejected', { defaultValue: 'Refund Rejected' })}
                  </p>
                  <p className="text-xs text-red-700 font-[CeraPro] mt-1">
                    {t('billing.refundRejectedMessage')}
                  </p>
                  {refundRequest?.reason && (
                    <p className="text-[11px] text-red-700 font-[CeraPro] mt-2">
                      {t('billing.yourNote', { defaultValue: 'Your note' })}: {refundRequest.reason}
                    </p>
                  )}
                  {refundRequest?.rejectionReason && (
                    <p className="text-[11px] text-red-700 font-[CeraPro] mt-1">
                      {t('billing.adminMessage', { defaultValue: 'Admin message' })}: {refundRequest.rejectionReason}
                    </p>
                  )}
                </div>
              )}
              {refundRequest?.status === 'approved' && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-sm font-bold text-emerald-700 font-[CeraPro]">
                    {t('billing.refundApproved', { defaultValue: 'Refund Approved' })}
                  </p>
                  <p className="text-xs text-emerald-700 font-[CeraPro] mt-1">
                    {t('billing.refundApprovedMessage', {
                      defaultValue: 'Your refund has been approved and is being returned to your original payment method.'
                    })}
                  </p>
                  {refundRequest?.processedAt && (
                    <p className="text-[11px] text-emerald-700 font-[CeraPro] mt-2">
                      {t('billing.processedOn', { defaultValue: 'Processed on' })}: {formatLocale(refundRequest.processedAt, 'MMM d, yyyy')}
                    </p>
                  )}
                  {refundRequest?.providerRefundId && (
                    <p className="text-[11px] text-emerald-700 font-[CeraPro] mt-1 break-all">
                      {t('billing.refundReference', { defaultValue: 'Reference' })}: {refundRequest.providerRefundId}
                    </p>
                  )}
                  {refundRequest?.reason && (
                    <p className="text-[11px] text-emerald-700 font-[CeraPro] mt-1">
                      {t('billing.yourNote', { defaultValue: 'Your note' })}: {refundRequest.reason}
                    </p>
                  )}
                  {refundRequest?.approvalReason && (
                    <p className="text-[11px] text-emerald-700 font-[CeraPro] mt-1">
                      {t('billing.adminMessage', { defaultValue: 'Admin message' })}: {refundRequest.approvalReason}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ModalV2>
  );
};

export default SubscriptionDetailModal;
